import React from 'react';
import { LucideIcon, Loader2 } from 'lucide-react';

export interface ButtonProps {
  children?: React.ReactNode;
  /**
   * Widened from `() => void` so a button inside a clickable card/row can call
   * `e.stopPropagation()`. Existing zero-arg handlers remain assignable.
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Button variant:
   * - primary: the brand action (primary-700)
   * - secondary, danger, ghost, outline: standard variants
   *
   * P2-08: `primaryCitizen` is gone. Its comment claimed it was "the same as
   * primary now", but it rendered primary-600 against primary's primary-700, so
   * a citizen screen's buttons were a shade lighter than the screen beside it.
   * The four call sites (all in JoinOrganizationPage) already passed size="lg",
   * which is where the padding it also claimed actually came from.
   */
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  className?: string;
  fullWidth?: boolean;
  ariaLabel?: string;
  title?: string;
  /** Remove shadow (legacy citizen forms don't have shadow) */
  noShadow?: boolean;
  /** For menu triggers (ExportMenu et al.) — announced with aria-expanded. */
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-expanded'?: boolean;
}

/*
 * forwardRef so a menu owner can return focus to its trigger on Escape —
 * ExportMenu is the first consumer. Everything else is unchanged.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  type = 'button',
  form,
  className = '',
  fullWidth = false,
  ariaLabel,
  title,
  noShadow = false,
  'aria-haspopup': ariaHasPopup,
  'aria-expanded': ariaExpanded,
}, ref) => {
  const baseStyles = `inline-flex items-center justify-center gap-2 font-medium rounded-lg whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-background-dark-primary disabled:opacity-50 disabled:cursor-not-allowed ${noShadow ? '' : 'shadow-sm'}`;

  const variantStyles = {
    primary: 'bg-primary-700 text-white hover:bg-primary-800 focus:ring-primary-500',
    secondary: 'bg-background-tertiary text-text-primary hover:bg-background-quaternary focus:ring-primary-500 dark:bg-background-dark-tertiary dark:text-text-dark-primary dark:hover:bg-background-dark-quaternary',
    danger: 'bg-error-600 text-white hover:bg-error-700 focus:ring-error-500 dark:bg-error-600 dark:hover:bg-error-700',
    ghost: 'bg-transparent text-text-secondary hover:bg-background-tertiary focus:ring-primary-500 dark:text-text-dark-secondary dark:hover:bg-background-dark-tertiary',
    outline: 'bg-white border border-border-light text-text-primary hover:bg-background-tertiary focus:ring-primary-500 dark:bg-background-dark-secondary dark:border-border-dark dark:text-text-dark-primary dark:hover:bg-background-dark-tertiary',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-4 py-3 text-base',
  };

  const iconSizeClass = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={isDisabled}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthClass} ${className}`}
      ref={ref}
      aria-label={ariaLabel}
      aria-busy={loading}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      title={title}
    >
      {loading && iconPosition === 'left' && (
        <Loader2 className={`${iconSizeClass[size]} animate-spin`} aria-hidden="true" />
      )}
      {!loading && Icon && iconPosition === 'left' && (
        <Icon className={iconSizeClass[size]} aria-hidden="true" />
      )}

      {children}

      {loading && iconPosition === 'right' && (
        <Loader2 className={`${iconSizeClass[size]} animate-spin`} aria-hidden="true" />
      )}
      {!loading && Icon && iconPosition === 'right' && (
        <Icon className={iconSizeClass[size]} aria-hidden="true" />
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
